import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { Settings as SettingsIcon, Save, Factory, Truck, Plus, Trash2, Package, Mail, Download, Send, Clock } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Settings() {
  const [settings, setSettings] = useState({
    fly_ash_rate: 0.90,
    interlock_6_rate: 5.50,
    interlock_8_rate: 6.50,
    loading_fly_ash_rate: 0.50,
    loading_interlock_6_rate: 2.00,
    loading_interlock_8_rate: 3.00,
    custom_materials: [],
    backup_email: '',
    backup_enabled: false
  });
  const [loading, setLoading] = useState(true);
  const [sendingBackup, setSendingBackup] = useState(false);
  const [backupStatus, setBackupStatus] = useState(null);

  useEffect(() => {
    fetchSettings();
    fetchBackupStatus();
  }, []);

  const fetchBackupStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/backup/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBackupStatus(response.data);
    } catch (error) {
      console.error('Error fetching backup status:', error);
    }
  };

  const fetchSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSettings({
        ...response.data,
        custom_materials: response.data.custom_materials || [],
        backup_email: response.data.backup_email || '',
        backup_enabled: response.data.backup_enabled || false
      });
    } catch (error) {
      console.error('Error fetching settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const addCustomMaterial = () => {
    setSettings({
      ...settings,
      custom_materials: [...settings.custom_materials, { 
        id: Date.now().toString(), 
        name: '', 
        rate: 0,
        loading_rate: 0 
      }]
    });
  };

  const updateCustomMaterial = (index, field, value) => {
    const updated = [...settings.custom_materials];
    updated[index][field] = value;
    setSettings({ ...settings, custom_materials: updated });
  };

  const removeCustomMaterial = (index) => {
    setSettings({
      ...settings,
      custom_materials: settings.custom_materials.filter((_, i) => i !== index)
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate custom materials
    const validMaterials = settings.custom_materials.filter(m => m.name.trim());
    
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/settings`, {
        ...settings,
        custom_materials: validMaterials
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Settings updated successfully');
      setSettings({ ...settings, custom_materials: validMaterials });
    } catch (error) {
      toast.error('Failed to update settings');
    }
  };

  const sendBackupNow = async () => {
    if (!settings.backup_email) {
      toast.error('Please enter a backup email address first');
      return;
    }
    
    setSendingBackup(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/backup/send`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Backup sent to ${settings.backup_email}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send backup');
    } finally {
      setSendingBackup(false);
    }
  };

  const downloadBackup = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/backup/download`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `DHAYA_BRICKS_Backup_${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Backup downloaded');
    } catch (error) {
      toast.error('Failed to download backup');
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-96">
      <p className="font-inter text-concrete-600">Loading settings...</p>
    </div>;
  }

  return (
    <div data-testid="settings-page" className="space-y-8">
      <div>
        <h1 className="font-manrope font-bold text-4xl text-concrete-900 tracking-tight" data-testid="settings-title">
          <SettingsIcon className="inline-block mr-3 h-10 w-10 text-brick-600" />
          Settings
        </h1>
        <p className="font-inter text-concrete-600 mt-2">Configure piece rates for production and loading</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Standard Brick Production Rates */}
        <div className="bg-white border border-concrete-200 shadow-sm rounded-md p-8">
          <div className="flex items-center gap-3 mb-6">
            <Factory className="h-6 w-6 text-brick-600" />
            <div>
              <h2 className="font-manrope font-bold text-xl text-concrete-900">Standard Brick Rates</h2>
              <p className="text-sm text-concrete-600">Default rates for standard brick types (salary calculation)</p>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="border border-concrete-200 rounded-lg p-4">
              <Label className="font-manrope font-semibold text-concrete-700">Fly Ash Bricks</Label>
              <p className="text-xs text-concrete-500 mb-2">₹ per piece</p>
              <Input
                type="number"
                step="0.01"
                min="0"
                data-testid="fly-ash-rate"
                value={settings.fly_ash_rate}
                onChange={(e) => setSettings({ ...settings, fly_ash_rate: parseFloat(e.target.value) || 0 })}
                className="border-concrete-300 focus:border-brick-500 tabular-nums text-lg font-semibold"
                required
              />
            </div>

            <div className="border border-concrete-200 rounded-lg p-4">
              <Label className="font-manrope font-semibold text-concrete-700">6" Interlock Bricks</Label>
              <p className="text-xs text-concrete-500 mb-2">₹ per piece</p>
              <Input
                type="number"
                step="0.01"
                min="0"
                data-testid="interlock-6-rate"
                value={settings.interlock_6_rate}
                onChange={(e) => setSettings({ ...settings, interlock_6_rate: parseFloat(e.target.value) || 0 })}
                className="border-concrete-300 focus:border-brick-500 tabular-nums text-lg font-semibold"
                required
              />
            </div>

            <div className="border border-concrete-200 rounded-lg p-4">
              <Label className="font-manrope font-semibold text-concrete-700">8" Interlock Bricks</Label>
              <p className="text-xs text-concrete-500 mb-2">₹ per piece</p>
              <Input
                type="number"
                step="0.01"
                min="0"
                data-testid="interlock-8-rate"
                value={settings.interlock_8_rate}
                onChange={(e) => setSettings({ ...settings, interlock_8_rate: parseFloat(e.target.value) || 0 })}
                className="border-concrete-300 focus:border-brick-500 tabular-nums text-lg font-semibold"
                required
              />
            </div>
          </div>
        </div>

        {/* Standard Loading Rates */}
        <div className="bg-white border border-concrete-200 shadow-sm rounded-md p-8">
          <div className="flex items-center gap-3 mb-6">
            <Truck className="h-6 w-6 text-green-600" />
            <div>
              <h2 className="font-manrope font-bold text-xl text-concrete-900">Standard Loading Rates</h2>
              <p className="text-sm text-concrete-600">Default rates for loading charges during sales</p>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="border border-concrete-200 rounded-lg p-4">
              <Label className="font-manrope font-semibold text-concrete-700">Fly Ash Loading</Label>
              <p className="text-xs text-concrete-500 mb-2">₹ per piece</p>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={settings.loading_fly_ash_rate}
                onChange={(e) => setSettings({ ...settings, loading_fly_ash_rate: parseFloat(e.target.value) || 0 })}
                className="border-concrete-300 focus:border-brick-500 tabular-nums text-lg font-semibold"
                required
              />
            </div>

            <div className="border border-concrete-200 rounded-lg p-4">
              <Label className="font-manrope font-semibold text-concrete-700">6" Interlock Loading</Label>
              <p className="text-xs text-concrete-500 mb-2">₹ per piece</p>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={settings.loading_interlock_6_rate}
                onChange={(e) => setSettings({ ...settings, loading_interlock_6_rate: parseFloat(e.target.value) || 0 })}
                className="border-concrete-300 focus:border-brick-500 tabular-nums text-lg font-semibold"
                required
              />
            </div>

            <div className="border border-concrete-200 rounded-lg p-4">
              <Label className="font-manrope font-semibold text-concrete-700">8" Interlock Loading</Label>
              <p className="text-xs text-concrete-500 mb-2">₹ per piece</p>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={settings.loading_interlock_8_rate}
                onChange={(e) => setSettings({ ...settings, loading_interlock_8_rate: parseFloat(e.target.value) || 0 })}
                className="border-concrete-300 focus:border-brick-500 tabular-nums text-lg font-semibold"
                required
              />
            </div>
          </div>
        </div>

        {/* Custom Material Types */}
        <div className="bg-white border border-concrete-200 shadow-sm rounded-md p-8">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <Package className="h-6 w-6 text-purple-600" />
              <div>
                <h2 className="font-manrope font-bold text-xl text-concrete-900">Additional Brick/Material Types</h2>
                <p className="text-sm text-concrete-600">Add custom brick types with production & loading rates</p>
              </div>
            </div>
            <Button type="button" variant="outline" onClick={addCustomMaterial}>
              <Plus className="h-4 w-4 mr-2" />
              Add New Type
            </Button>
          </div>
          
          {settings.custom_materials.length === 0 ? (
            <div className="text-center py-8 bg-concrete-50 rounded-lg">
              <Package className="h-12 w-12 text-concrete-400 mx-auto mb-3" />
              <p className="text-concrete-600">No custom materials added yet</p>
              <p className="text-sm text-concrete-500 mt-1">Click "Add New Type" to create custom brick types</p>
            </div>
          ) : (
            <div className="space-y-4">
              {settings.custom_materials.map((material, index) => (
                <div key={material.id || index} className="bg-purple-50 border border-purple-200 p-4 rounded-lg">
                  <div className="flex gap-4 items-start">
                    <div className="flex-1">
                      <Label className="text-xs font-semibold text-purple-700">Material / Brick Name</Label>
                      <Input
                        value={material.name}
                        onChange={(e) => updateCustomMaterial(index, 'name', e.target.value)}
                        placeholder="e.g., Paver Block, 4 inch Solid"
                        className="mt-1 bg-white"
                      />
                    </div>
                    <div className="w-36">
                      <Label className="text-xs font-semibold text-purple-700">Production Rate</Label>
                      <div className="flex items-center gap-1 mt-1">
                        <span className="text-sm text-concrete-600">₹</span>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          value={material.rate}
                          onChange={(e) => updateCustomMaterial(index, 'rate', parseFloat(e.target.value) || 0)}
                          className="bg-white tabular-nums"
                          placeholder="0.00"
                        />
                      </div>
                      <p className="text-xs text-concrete-500 mt-1">per piece (salary)</p>
                    </div>
                    <div className="w-36">
                      <Label className="text-xs font-semibold text-green-700">Loading Rate</Label>
                      <div className="flex items-center gap-1 mt-1">
                        <span className="text-sm text-concrete-600">₹</span>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          value={material.loading_rate || 0}
                          onChange={(e) => updateCustomMaterial(index, 'loading_rate', parseFloat(e.target.value) || 0)}
                          className="bg-white tabular-nums"
                          placeholder="0.00"
                        />
                      </div>
                      <p className="text-xs text-concrete-500 mt-1">per piece (loading)</p>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => removeCustomMaterial(index)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50 mt-6"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="mt-4 bg-purple-50 border border-purple-200 rounded-md p-4">
            <p className="font-inter text-sm text-purple-800">
              <strong>Note:</strong> Custom materials will appear in Production (with production rate) and Sales loading charges (with loading rate).
            </p>
          </div>
        </div>

        {/* Daily Email Backup Settings */}
        <div className="bg-white border border-concrete-200 shadow-sm rounded-md p-8">
          <div className="flex items-center gap-3 mb-6">
            <Mail className="h-6 w-6 text-blue-600" />
            <div>
              <h2 className="font-manrope font-bold text-xl text-concrete-900">Daily Email Backup</h2>
              <p className="text-sm text-concrete-600">Automatically receive daily backup PDF at 11:59 PM</p>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div>
              <Label className="font-manrope font-semibold text-concrete-700">Backup Email Address</Label>
              <p className="text-xs text-concrete-500 mb-2">Backup PDF will be sent to this email</p>
              <Input
                type="email"
                value={settings.backup_email}
                onChange={(e) => setSettings({ ...settings, backup_email: e.target.value })}
                placeholder="your-email@example.com"
                className="border-concrete-300 focus:border-blue-500"
              />
            </div>
            <div>
              <Label className="font-manrope font-semibold text-concrete-700">Enable Daily Backup</Label>
              <p className="text-xs text-concrete-500 mb-2">Automatic backup at 11:59 PM daily</p>
              <div className="flex items-center gap-3 mt-2">
                <Switch
                  checked={settings.backup_enabled}
                  onCheckedChange={(checked) => setSettings({ ...settings, backup_enabled: checked })}
                />
                <span className={`text-sm font-medium ${settings.backup_enabled ? 'text-green-600' : 'text-concrete-500'}`}>
                  {settings.backup_enabled ? 'Enabled' : 'Disabled'}
                </span>
              </div>
              {backupStatus?.next_scheduled_backup && settings.backup_enabled && (
                <p className="text-xs text-green-600 mt-2">
                  Next backup: {new Date(backupStatus.next_scheduled_backup).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })}
                </p>
              )}
            </div>
          </div>

          <div className="flex flex-wrap gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={sendBackupNow}
              disabled={sendingBackup || !settings.backup_email}
              className="border-blue-300 hover:border-blue-500 hover:bg-blue-50"
            >
              <Send className="h-4 w-4 mr-2" />
              {sendingBackup ? 'Sending...' : 'Send Backup Now'}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={downloadBackup}
              className="border-green-300 hover:border-green-500 hover:bg-green-50"
            >
              <Download className="h-4 w-4 mr-2" />
              Download Backup PDF
            </Button>
          </div>

          <div className="mt-4 bg-blue-50 border border-blue-200 rounded-md p-4">
            <div className="flex items-start gap-2">
              <Clock className="h-4 w-4 text-blue-600 mt-0.5" />
              <div>
                <p className="font-inter text-sm text-blue-800">
                  <strong>Backup includes:</strong> Stock status, Workers & Salary, Customers, Recent Production (30 days), Recent Sales (30 days), and Settings.
                </p>
                <p className="font-inter text-xs text-blue-600 mt-1">
                  Daily backups are sent at 11:59 PM IST when enabled.
                </p>
              </div>
            </div>
          </div>
        </div>

        <Button
          type="submit"
          data-testid="save-settings-button"
          className="bg-brick-600 hover:bg-brick-500 text-white shadow-md hover:shadow-lg transition-all duration-200 font-manrope font-semibold rounded-md px-8 py-3"
        >
          <Save className="mr-2 h-5 w-5" />
          Save All Settings
        </Button>
      </form>

      {/* Current Rates Summary */}
      <div className="bg-white border border-concrete-200 shadow-sm rounded-md p-8">
        <h2 className="font-manrope font-bold text-xl text-concrete-900 mb-4">All Configured Rates Summary</h2>
        
        <h3 className="font-semibold text-concrete-700 mb-2">Production Rates (Salary)</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <div className="bg-brick-50 rounded-md p-3">
            <p className="font-inter text-xs text-concrete-600 uppercase tracking-wider mb-1">Fly Ash</p>
            <p className="font-manrope font-bold text-lg text-brick-600 tabular-nums">₹{settings.fly_ash_rate.toFixed(2)}</p>
          </div>
          <div className="bg-brick-50 rounded-md p-3">
            <p className="font-inter text-xs text-concrete-600 uppercase tracking-wider mb-1">6" Interlock</p>
            <p className="font-manrope font-bold text-lg text-brick-600 tabular-nums">₹{settings.interlock_6_rate.toFixed(2)}</p>
          </div>
          <div className="bg-brick-50 rounded-md p-3">
            <p className="font-inter text-xs text-concrete-600 uppercase tracking-wider mb-1">8" Interlock</p>
            <p className="font-manrope font-bold text-lg text-brick-600 tabular-nums">₹{settings.interlock_8_rate.toFixed(2)}</p>
          </div>
          {settings.custom_materials.filter(m => m.name).map((material, index) => (
            <div key={index} className="bg-purple-50 rounded-md p-3">
              <p className="font-inter text-xs text-concrete-600 uppercase tracking-wider mb-1">{material.name}</p>
              <p className="font-manrope font-bold text-lg text-purple-600 tabular-nums">₹{(material.rate || 0).toFixed(2)}</p>
            </div>
          ))}
        </div>

        <h3 className="font-semibold text-concrete-700 mb-2">Loading Rates</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="bg-green-50 rounded-md p-3">
            <p className="font-inter text-xs text-concrete-600 uppercase tracking-wider mb-1">Fly Ash</p>
            <p className="font-manrope font-bold text-lg text-green-600 tabular-nums">₹{settings.loading_fly_ash_rate.toFixed(2)}</p>
          </div>
          <div className="bg-green-50 rounded-md p-3">
            <p className="font-inter text-xs text-concrete-600 uppercase tracking-wider mb-1">6" Interlock</p>
            <p className="font-manrope font-bold text-lg text-green-600 tabular-nums">₹{settings.loading_interlock_6_rate.toFixed(2)}</p>
          </div>
          <div className="bg-green-50 rounded-md p-3">
            <p className="font-inter text-xs text-concrete-600 uppercase tracking-wider mb-1">8" Interlock</p>
            <p className="font-manrope font-bold text-lg text-green-600 tabular-nums">₹{settings.loading_interlock_8_rate.toFixed(2)}</p>
          </div>
          {settings.custom_materials.filter(m => m.name).map((material, index) => (
            <div key={index} className="bg-green-50 rounded-md p-3">
              <p className="font-inter text-xs text-concrete-600 uppercase tracking-wider mb-1">{material.name}</p>
              <p className="font-manrope font-bold text-lg text-green-600 tabular-nums">₹{(material.loading_rate || 0).toFixed(2)}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
